'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { RailItem, type RailIcon } from './rail-item';

/**
 * A rail button that opens a small flyout panel to its right. Shared by
 * Create, Tools, and Zoom so all three pop the same way (the user asked
 * for Create to behave like Tools). Closes on outside pointerdown or Esc.
 */
export function RailFlyout({
  icon,
  label,
  active,
  align = 'top',
  testId,
  onboardingAnchor,
  render,
}: {
  icon: RailIcon;
  label: string;
  active?: boolean;
  /** Vertical anchor of the flyout relative to the button. */
  align?: 'top' | 'bottom';
  testId?: string;
  onboardingAnchor?: string;
  render: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <RailItem
        icon={icon}
        label={label}
        active={active || open}
        testId={testId}
        onboardingAnchor={onboardingAnchor}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          className={cn(
            'absolute left-full z-50 ml-2 min-w-[180px] rounded-xl border border-border/40 bg-popover py-1 shadow-lg',
            align === 'bottom' ? 'bottom-0' : 'top-0',
          )}
          role="menu"
        >
          {render(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function FlyoutRow({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: RailIcon;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors hover:bg-accent',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
      <span className="flex-1">{label}</span>
      {active && (
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
      )}
    </button>
  );
}
